import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Params, Router, UrlSegment } from '@angular/router';
import { Subject } from 'rxjs/Subject';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/forkJoin';
import 'rxjs/add/operator/takeUntil';
import { TranslateService } from '@ngx-translate/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import {
  SkillDocumentResponse,
  SkillSpecializationResponse,
  SkillAvailableCurator,
  SkillGroup,
  SkillSpecializationCreateRequest,
  TheoryLevelRequest,
  PracticeLevelRequest,
  SkillCuratorAssignmentRequest,
  SkillTestQuestion,
  SpecializationFormValue,
  EditSpecializationMessageParams,
  SkillLevelStructureUpdateRequest,
  SkillLearningErrorResponse,
  SkillLearningHttpError,
  SkillRequirementDeleteCommand,
  SkillRequirementUpdateCommand,
  SkillTrainingRequirementScope,
  SkillUploadDocumentCommand,
} from '../../../models/skill-learning.model';
import { SkillDocumentRefreshError, SkillSpecializationFacadeService } from '../../../services/skill-specialization-facade.service';
import { ToastService } from '../../../../../../services/toast.service';
import { SidebarService } from '../../../../../../services/side-bar.service';
import {
  TheoryLevelSavePayload,
  TheoryTestGenerationPayload,
} from '../theory-level-editor/theory-level-editor.option';
import { PracticeLevelSavePayload } from '../practice-level-editor/practice-level-editor.component';
import { SkillDocumentPreviewModalComponent } from '../../shared/skill-document-preview-modal/skill-document-preview-modal.component';
import { SPECIALIZATION_CURATOR_SELECTION } from '../specialization-form/specialization-form.option';
import { EditSpecializationGeneralPageComponent } from '../edit-specialization-general-page/edit-specialization-general-page.component';
import {
  SKILL_CONFIGURATION_ERROR,
  SKILL_DEPARTMENT_QUERY_PARAM,
  SKILL_HTTP_STATUS,
  SKILL_LEARNING_EMPTY_TEXT,
  SKILL_LEARNING_LEVEL_TAB,
  SKILL_LEARNING_LEVEL_COUNT,
  SKILL_LEARNING_EDIT_MESSAGE,
  SKILL_LEARNING_EDIT_PAGE,
  SKILL_LEARNING_FIRST_INDEX,
  SKILL_LEARNING_LAST_INDEX_OFFSET,
  SKILL_LEARNING_ROUTE,
  SKILL_LEARNING_ROUTE_PART,
  SKILL_LEARNING_SIDEBAR,
  SKILL_SPECIALIZATION_STATUS_LABEL_PREFIX,
  EditSpecializationPage,
  SkillLearningLevelTab,
  SKILL_REQUIREMENT_MESSAGE,
  SKILL_REQUIREMENT_SCOPE,
  SKILL_REQUIREMENT_SCOPE_LABEL,
  SKILL_STRUCTURE_MESSAGE,
  SKILL_TEST_ERROR_TOKEN,
  SKILL_TEST_MESSAGE,
} from '../../../options/skill-learning.option';

const HEAD_REQUIREMENT_CONFLICT = 'SKILL_HEAD_REQUIREMENT_CONFLICT';

@Component({
  selector: 'app-edit-specialization-container',
  templateUrl: './edit-specialization-container.component.html',
  styleUrls: ['./edit-specialization-container.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditSpecializationContainerComponent implements OnInit, OnDestroy {

  @ViewChild(EditSpecializationGeneralPageComponent)
  generalPage: EditSpecializationGeneralPageComponent;

  readonly pages = SKILL_LEARNING_EDIT_PAGE;
  readonly routes = SKILL_LEARNING_ROUTE;
  readonly routeParts = SKILL_LEARNING_ROUTE_PART;

  specialization: SkillSpecializationResponse | null = null;
  availableCurators: SkillAvailableCurator[] = [];
  availableFillAssignees: SkillAvailableCurator[] = [];
  availableGroups: SkillGroup[] = [];
  theoryDirectionsPrompt = SKILL_LEARNING_EMPTY_TEXT;
  activeLevelTab: SkillLearningLevelTab = SKILL_LEARNING_LEVEL_TAB.LEVEL_1;
  page: EditSpecializationPage = SKILL_LEARNING_EDIT_PAGE.GENERAL;
  isLoading = false;
  isSubmitting = false;
  isGeneratingTheoryTests = false;
  selectedDepartmentId = SKILL_LEARNING_EMPTY_TEXT;
  structureErrorKey = SKILL_LEARNING_EMPTY_TEXT;
  requirementErrorKey = SKILL_LEARNING_EMPTY_TEXT;
  requirementErrorScope: SkillTrainingRequirementScope | null = null;
  pendingRequirementScope: SkillTrainingRequirementScope | null = null;
  pendingDepartmentId = SKILL_LEARNING_EMPTY_TEXT;
  showDepartmentDiscardConfirmation = false;
  hasLoadError = false;
  isLoadDenied = false;

  readonly requirementScopeLabels = SKILL_REQUIREMENT_SCOPE_LABEL;

  private specializationId = SKILL_LEARNING_EMPTY_TEXT;
  private detailRequestId = SKILL_LEARNING_FIRST_INDEX;
  private curatorRequestId = SKILL_LEARNING_FIRST_INDEX;
  private theoryGenerationRequestId = SKILL_LEARNING_FIRST_INDEX;
  private activeTheoryGenerationRequestId = SKILL_LEARNING_FIRST_INDEX;
  private activeTheoryGenerationLevel = SKILL_LEARNING_FIRST_INDEX;
  private generatedTheoryTestQuestionsByLevel: SkillTestQuestion[][] = [];
  private readonly destroy$ = new Subject<void>();
  private readonly specializationContextChanged$ = new Subject<void>();

  constructor(
    private facade: SkillSpecializationFacadeService,
    private toastService: ToastService,
    private sidebarService: SidebarService,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private modalService?: NgbModal,
  ) {}

  get canManageCurators(): boolean {
    return !!this.specialization && !!this.specialization.canManageCurators;
  }

  get canAssignAnyCuratorLevel(): boolean {
    return this.canManageCurators;
  }

  get canEditContent(): boolean {
    return !!this.specialization && !!this.specialization.canEditContent;
  }

  get canChangeLevelStructure(): boolean {
    return !!this.specialization && !!this.specialization.canChangeLevelStructure;
  }

  get canOpenLevelsPage(): boolean {
    return this.canEditContent || this.canChangeLevelStructure;
  }

  get activeLevelNumber(): number {
    return this.activeLevelTab + SKILL_LEARNING_LEVEL_COUNT.ONE;
  }

  get generatedTheoryTestQuestions(): SkillTestQuestion[] {
    return this.generatedTheoryTestQuestionsByLevel[this.activeLevelNumber] || [];
  }

  get canManageParticipantGroups(): boolean {
    return !!this.specialization && !!this.specialization.canManageParticipantGroups;
  }

  get practice2DocumentFileId(): string | null {
    if (!this.specialization || !this.specialization.practiceLevel2) {
      return null;
    }
    return this.specialization.practiceLevel2.documentFileId;
  }

  get practice2Document(): SkillDocumentResponse | null {
    if (!this.specialization || !this.specialization.practiceLevel2) {
      return null;
    }
    return this.specialization.practiceLevel2.document;
  }

  get practice3DocumentFileId(): string | null {
    if (!this.specialization || !this.specialization.practiceLevel3) {
      return null;
    }
    return this.specialization.practiceLevel3.documentFileId;
  }

  get practice3Document(): SkillDocumentResponse | null {
    if (!this.specialization || !this.specialization.practiceLevel3) {
      return null;
    }
    return this.specialization.practiceLevel3.document;
  }

  ngOnInit(): void {
    this.sidebarService.setSelectedMenuOption(SKILL_LEARNING_SIDEBAR.SPECIALIZATIONS);
    this.subscribeToDepartmentContext();
    this.subscribeToSpecializationId();
    this.subscribeToPage();
  }

  ngOnDestroy(): void {
    this.resetTheoryGenerationState();
    this.destroy$.next();
    this.destroy$.complete();
    this.specializationContextChanged$.complete();
  }

  get isGeneralPage(): boolean {
    return this.page === SKILL_LEARNING_EDIT_PAGE.GENERAL || !this.canOpenLevelsPage;
  }

  get isLevelsPage(): boolean {
    return this.page === SKILL_LEARNING_EDIT_PAGE.LEVELS && this.canOpenLevelsPage;
  }

  onGeneralInfoSaved(value: SpecializationFormValue): void {
    if (this.isLoading || this.isSubmitting || this.hasLoadError) { return; }
    if (!this.canEditContent && !this.canManageParticipantGroups) {
      this.onCuratorsChanged(value.curators, value.responsibleGroupId);
      return;
    }
    const request = this.buildGeneralInfoRequest(value);
    this.isSubmitting = true;
    if (this.shouldUpdateGeneralInfoOnly(value)) {
      this.updateGeneralInfo(request);
      return;
    }
    this.updateGeneralInfoWithCurators(request, value);
  }

  onRequirementUpdated(command: SkillRequirementUpdateCommand): void {
    if (!this.beginRequirementCommand(command.scope)) {
      return;
    }
    this.facade.updateRequirement(this.specializationId, command)
      .takeUntil(this.specializationContextChanged$).takeUntil(this.destroy$)
      .subscribe(
        specialization => this.handleRequirementSuccess(specialization),
        error => this.handleRequirementError(error, command.scope),
      );
  }

  get specializationStatusLabelKey(): string {
    return this.specialization
      ? SKILL_SPECIALIZATION_STATUS_LABEL_PREFIX + this.specialization.status
      : SKILL_LEARNING_EMPTY_TEXT;
  }

  get selectedDepartmentName(): string {
    if (!this.selectedDepartmentId) {
      return SKILL_LEARNING_EMPTY_TEXT;
    }
    const group = this.availableGroups.find(item => item.id === this.selectedDepartmentId);
    return group ? group.name : this.selectedDepartmentId;
  }

  onRequirementDeleted(command: SkillRequirementDeleteCommand): void {
    if (!this.beginRequirementCommand(command.scope, true)) {
      return;
    }
    this.facade.deleteRequirement(this.specializationId, command)
      .takeUntil(this.specializationContextChanged$).takeUntil(this.destroy$)
      .subscribe(
        specialization => this.handleRequirementSuccess(specialization),
        error => this.handleRequirementError(error, command.scope),
      );
  }

  onStructureChanged(request: SkillLevelStructureUpdateRequest): void {
    this.isSubmitting = true;
    this.structureErrorKey = SKILL_LEARNING_EMPTY_TEXT;
    this.facade.updateStructure(this.specializationId, request)
      .takeUntil(this.destroy$)
      .subscribe(
        specialization => this.handleStructureSuccess(specialization),
        error => this.handleStructureError(error),
      );
  }

  onCuratorsChanged(curators: SkillCuratorAssignmentRequest[], responsibleGroupId?: string): void {
    if (this.isLoading || this.isSubmitting || !this.canManageCurators) { return; }
    this.isSubmitting = true;
    this.facade.assignCurators(
      this.specializationId,
      { curators, responsibleGroupId },
      this.selectedDepartmentId || undefined,
    )
      .takeUntil(this.specializationContextChanged$).takeUntil(this.destroy$)
      .subscribe(
        specialization => {
          this.specialization = specialization;
          this.loadAvailableCurators();
          this.handleSaveSuccess(SKILL_LEARNING_EDIT_MESSAGE.CURATORS_UPDATED);
        },
        error => this.handleCuratorSaveError(error),
      );
  }

  onTheoryLevelSaved(payload: TheoryLevelSavePayload): void {
    if (!this.specialization) {
      return;
    }
    this.isSubmitting = true;
    const request: TheoryLevelRequest = Object.assign({}, payload.request, {
      expectedRevision: this.specialization.configurationRevision,
    });
    this.facade.updateTheoryLevel(
      this.specializationId,
      payload.levelNumber,
      request,
    )
      .takeUntil(this.destroy$)
      .subscribe(
        () => this.handleContentSaved(payload.levelNumber),
        error => this.handleContentSaveError(error),
      );
  }

  onTheoryTestQuestionsGenerationRequested(payload: TheoryTestGenerationPayload): void {
    if (this.isGeneratingTheoryTests) {
      return;
    }
    const level = payload.levelNumber;
    const requestId = this.activeTheoryGenerationRequestId = ++this.theoryGenerationRequestId;
    this.activeTheoryGenerationLevel = level;
    this.isGeneratingTheoryTests = true;
    this.facade.generateTheoryTestQuestions(
      this.specializationId,
      level,
      { description: payload.description },
    )
      .takeUntil(this.destroy$)
      .subscribe(
        questions => this.handleTheoryQuestionGenerationSuccess(questions, level, requestId),
        error => this.handleTheoryQuestionGenerationError(error, level, requestId),
      );
  }

  onActiveLevelTabChanged(tab: SkillLearningLevelTab): void {
    this.activeLevelTab = tab;
    this.loadTheoryDirectionsPrompt(this.activeLevelNumber);
  }

  onPracticeLevel2Saved(payload: PracticeLevelSavePayload): void {
    this.savePracticeLevel(payload, SKILL_LEARNING_LEVEL_COUNT.TWO);
  }

  onPracticeLevel3Saved(payload: PracticeLevelSavePayload): void {
    this.savePracticeLevel(payload, SKILL_LEARNING_LEVEL_COUNT.THREE);
  }

  onDocumentDownload(level: number): void {
    const url = this.facade.buildDocumentDownloadUrl(this.specializationId, level);
    window.open(url, '_blank');
  }

  onDocumentPreview(level: number): void {
    const document = level === SKILL_LEARNING_LEVEL_COUNT.TWO
      ? this.practice2Document
      : this.practice3Document;
    if (!document) {
      this.onDocumentDownload(level);
      return;
    }
    if (!this.modalService) {
      this.onDocumentDownload(level);
      return;
    }
    const modalRef = this.modalService.open(SkillDocumentPreviewModalComponent, { size: 'lg' });
    modalRef.componentInstance.specializationId = this.specializationId;
    modalRef.componentInstance.level = level;
    modalRef.componentInstance.document = document;
  }

  onDepartmentChanged(departmentId: string): void {
    if (this.pendingRequirementScope) {
      return;
    }
    if (departmentId === this.selectedDepartmentId) {
      return;
    }
    if (this.generalPage && this.generalPage.hasDirtyDepartmentRequirement()) {
      this.pendingDepartmentId = departmentId;
      this.showDepartmentDiscardConfirmation = true;
      return;
    }
    this.applyDepartmentChange(departmentId);
  }

  confirmDepartmentChange(): void {
    if (this.generalPage) {
      this.generalPage.discardDepartmentRequirementChanges();
    }
    this.applyDepartmentChange(this.pendingDepartmentId);
    this.cancelDepartmentChange();
  }

  cancelDepartmentChange(): void {
    this.pendingDepartmentId = SKILL_LEARNING_EMPTY_TEXT;
    this.showDepartmentDiscardConfirmation = false;
  }

  onRequirementFocusRequested(): void {
    this.router.navigate(
      [
        SKILL_LEARNING_ROUTE.SPECIALIZATIONS,
        this.specializationId,
        SKILL_LEARNING_ROUTE_PART.EDIT,
        this.pages.GENERAL,
      ],
      { queryParams: this.departmentQueryParams },
    ).then(() => {
      this.cdr.detectChanges();
      this.focusRequirements();
    });
  }

  onCancelRequested(): void {
    this.router.navigate(
      [SKILL_LEARNING_ROUTE.SPECIALIZATIONS],
      { queryParams: this.departmentQueryParams },
    );
  }

  onReloadRequested(): void {
    this.loadSpecialization();
  }

  private subscribeToSpecializationId(): void {
    this.route.params
      .takeUntil(this.destroy$)
      .subscribe(params => this.handleSpecializationIdChange(params['id']));
  }

  private subscribeToDepartmentContext(): void {
    this.route.queryParams
      .takeUntil(this.destroy$)
      .subscribe(params => this.handleDepartmentContextChange(
        params[SKILL_DEPARTMENT_QUERY_PARAM] || SKILL_LEARNING_EMPTY_TEXT,
      ));
  }

  private subscribeToPage(): void {
    this.route.url
      .takeUntil(this.destroy$)
      .subscribe(segments => this.handlePageChange(segments));
  }

  private handleSpecializationIdChange(id: string): void {
    if (!id || this.specializationId === id) {
      return;
    }
    this.specializationId = id;
    this.theoryDirectionsPrompt = '';
    this.loadSpecialization();
  }

  private handleDepartmentContextChange(departmentId: string): void {
    if (this.selectedDepartmentId === departmentId) {
      return;
    }
    this.selectedDepartmentId = departmentId;
    this.curatorRequestId += SKILL_LEARNING_LEVEL_COUNT.ONE;
    this.availableCurators = [];
    this.requirementErrorKey = SKILL_LEARNING_EMPTY_TEXT;
    this.requirementErrorScope = null;
    this.structureErrorKey = SKILL_LEARNING_EMPTY_TEXT;
    if (this.specializationId) {
      this.loadSpecialization();
    }
  }

  private handlePageChange(segments: UrlSegment[]): void {
    const page = this.resolvePage(segments);
    if (this.page === page) {
      return;
    }
    this.page = page;
    this.cdr.markForCheck();
  }

  private resolvePage(segments: UrlSegment[]): EditSpecializationPage {
    if (!segments || segments.length === SKILL_LEARNING_FIRST_INDEX) {
      return SKILL_LEARNING_EDIT_PAGE.GENERAL;
    }
    const pageIndex = segments.length - SKILL_LEARNING_LAST_INDEX_OFFSET;
    return segments[pageIndex].path === SKILL_LEARNING_EDIT_PAGE.LEVELS
      ? SKILL_LEARNING_EDIT_PAGE.LEVELS
      : SKILL_LEARNING_EDIT_PAGE.GENERAL;
  }

  private handleTheoryQuestionGenerationSuccess(
    questions: SkillTestQuestion[],
    level: number,
    requestId: number,
  ): void {
    if (!this.isCurrentTheoryGeneration(level, requestId)) {
      return;
    }
    this.generatedTheoryTestQuestionsByLevel[level] = questions || [];
    this.completeTheoryGeneration();
    this.toastService.success(this.translate.instant(
      'SKILL_LEARNING_ADMIN_THEORY_TEST_BANK_GENERATED',
      { level },
    ));
    this.cdr.markForCheck();
  }

  private handleTheoryQuestionGenerationError(
    error: SkillLearningHttpError,
    level: number,
    requestId: number,
  ): void {
    if (!this.isCurrentTheoryGeneration(level, requestId)) {
      return;
    }
    this.completeTheoryGeneration();
    this.toastService.error(this.translate.instant(this.theoryGenerationErrorMessage(error)));
    this.cdr.markForCheck();
  }

  private savePracticeLevel(payload: PracticeLevelSavePayload, level: number): void {
    if (!this.specialization) {
      return;
    }
    this.isSubmitting = true;
    const request: PracticeLevelRequest = Object.assign({}, payload.request, {
      expectedRevision: this.specialization.configurationRevision,
    });
    this.facade.updatePracticeLevel(
      this.specializationId,
      level,
      request,
    )
      .takeUntil(this.destroy$)
      .subscribe(
        specialization => this.handlePracticeSaved(specialization, payload.documentFile, level),
        error => this.handleContentSaveError(error),
      );
  }

  private handlePracticeSaved(
    specialization: SkillSpecializationResponse,
    file: File | null,
    level: number,
  ): void {
    if (file) {
      this.uploadDocument(file, level, specialization.configurationRevision);
      return;
    }
    this.handleContentSaved(level);
  }

  private uploadDocument(file: File, level: number, expectedRevision: number): void {
    const command: SkillUploadDocumentCommand = {
      level,
      file,
      expectedRevision,
    };
    this.facade.uploadLevelDocument(
      this.specializationId,
      command,
      this.selectedDepartmentId || undefined,
    )
      .takeUntil(this.destroy$)
      .subscribe(
        specialization => this.handleContentSaved(level, specialization),
        error => this.handleDocumentUploadError(level, error),
      );
  }

  private handleDocumentUploadError(level: number, error: SkillLearningHttpError | SkillDocumentRefreshError): void {
    const message = error instanceof SkillDocumentRefreshError
      ? SKILL_LEARNING_EDIT_MESSAGE.DOCUMENT_REFRESH_FAILED
      : SKILL_LEARNING_EDIT_MESSAGE.DOCUMENT_UPLOAD_FAILED;
    this.toastService.error(this.translate.instant(
      message,
      { level },
    ));
    this.isSubmitting = false;
    this.loadSpecialization();
  }

  private loadSpecialization(): void {
    this.resetTheoryGenerationState();
    this.specializationContextChanged$.next();
    this.isSubmitting = false;
    this.pendingRequirementScope = null;
    const requestId = ++this.detailRequestId;
    this.isLoading = true;
    this.hasLoadError = false;
    this.isLoadDenied = false;
    this.facade.fetchSpecializationDetail(this.specializationId, this.selectedDepartmentId || undefined)
      .takeUntil(this.destroy$)
      .subscribe(
        spec => this.handleSpecializationLoaded(spec, requestId),
        error => this.handleLoadError(error, requestId),
      );
  }

  private loadAvailableCurators(): void {
    const requestId = ++this.curatorRequestId;
    this.clearAvailableCurators();
    if (!this.specialization || !this.canManageCurators) {
      return this.clearAvailableCurators();
    }
    const curatorGroupId = this.resolveCuratorCandidateGroupId();
    if (!curatorGroupId) {
      return this.clearAvailableCurators();
    }
    if (this.canAssignAnyCuratorLevel && this.specialization.crossDepartmental) {
      this.loadCrossDepartmentCurators(curatorGroupId, requestId);
      return;
    }
    if (this.canAssignAnyCuratorLevel) {
      this.loadCuratorGroupUsers(curatorGroupId, requestId);
      return;
    }
    this.loadEligibleCurators(curatorGroupId, requestId);
  }

  private resolveCuratorCandidateGroupId(): string {
    const manageableGroupIds = this.specialization.manageableCuratorGroupIds || [];
    return this.specialization.curatorGroupId
      || manageableGroupIds[SKILL_LEARNING_FIRST_INDEX]
      || this.specialization.groupId;
  }

  private loadCuratorGroupUsers(groupId: string, requestId: number): void {
    this.facade.fetchCuratorGroupUsers(groupId)
      .takeUntil(this.destroy$)
      .subscribe(
        users => {
          if (requestId !== this.curatorRequestId) {
            return;
          }
          this.availableCurators = users || [];
          this.cdr.markForCheck();
        },
        error => this.handleCuratorCandidatesError(requestId, error),
      );
  }

  private loadCrossDepartmentCurators(groupId: string, requestId: number): void {
    const manageableGroupIds = this.specialization.manageableCuratorGroupIds || [];
    const groupIds = (manageableGroupIds.length ? manageableGroupIds : [groupId])
      .filter((id, index, values) => !!id && values.indexOf(id) === index);
    const requests: Observable<SkillAvailableCurator[]>[] = groupIds
      .map(id => this.facade.fetchCuratorGroupUsers(id));
    requests.push(this.facade.fetchAvailableCuratorsForGroup(groupId, this.specializationId));
    Observable.forkJoin(requests)
      .takeUntil(this.destroy$)
      .subscribe(
        groups => {
          if (requestId !== this.curatorRequestId) {
            return;
          }
          this.availableCurators = this.mergeUniqueCurators(groups);
          this.cdr.markForCheck();
        },
        error => this.handleCuratorCandidatesError(requestId, error),
      );
  }

  private mergeUniqueCurators(groups: SkillAvailableCurator[][]): SkillAvailableCurator[] {
    const ids = new Set<string>();
    const result: SkillAvailableCurator[] = [];
    groups.forEach(curators => (curators || []).forEach(curator => {
      if (curator && curator.id && !ids.has(curator.id)) {
        ids.add(curator.id);
        result.push(curator);
      }
    }));
    return result;
  }

  private clearAvailableCurators(): void {
    this.availableCurators = [];
    this.cdr.markForCheck();
  }

  private loadEligibleCurators(groupId: string, requestId: number): void {
    this.facade.fetchAvailableCuratorsForGroup(groupId, this.specializationId)
      .takeUntil(this.destroy$)
      .subscribe(
        curators => {
          if (requestId !== this.curatorRequestId) {
            return;
          }
          this.availableCurators = curators;
          this.cdr.markForCheck();
        },
        error => this.handleCuratorCandidatesError(requestId, error),
      );
  }

  private handleCuratorCandidatesError(requestId: number, error: SkillLearningHttpError): void {
    if (requestId === this.curatorRequestId) {
      this.clearAvailableCurators();
      if (error && error.status === SKILL_HTTP_STATUS.FORBIDDEN && this.specialization) {
        this.specialization.canManageCurators = false;
      }
      this.handleGroupsLoadError();
    }
  }

  private loadFillAssignees(): void {
    if (!this.specialization || !this.specialization.groupId || !this.canEditContent) {
      this.availableFillAssignees = [];
      this.cdr.markForCheck();
      return;
    }
    this.facade.fetchGroupUsers(this.specialization.groupId)
      .takeUntil(this.destroy$)
      .subscribe(
        users => {
          this.availableFillAssignees = users || [];
          this.cdr.markForCheck();
        },
        () => this.cdr.markForCheck(),
      );
  }

  private loadGroups(): void {
    if (!this.shouldLoadGroups()) {
      this.availableGroups = [];
      return;
    }
    const groups = this.canManageParticipantGroups ? this.facade.fetchRoutingGroups() : this.facade.fetchGroups();
    groups.takeUntil(this.specializationContextChanged$).takeUntil(this.destroy$)
      .subscribe(
        groups => {
          this.availableGroups = groups || [];
          this.cdr.markForCheck();
        },
        () => this.handleGroupsLoadError(),
      );
  }

  private shouldLoadGroups(): boolean {
    const needsCuratorNames = this.canManageCurators
      && !!this.specialization && !!this.specialization.crossDepartmental;
    const needsOperationGroups = !!this.specialization
      && (!!this.selectedDepartmentId || !!(this.specialization.allowedStructureDepartmentIds || []).length
        || !!this.specialization.inheritedFromDepartmentId
        || !!this.specialization.canManageDepartmentProbationRequirement);
    return this.canManageParticipantGroups || needsCuratorNames || needsOperationGroups;
  }

  private buildGeneralInfoRequest(value: SpecializationFormValue): SkillSpecializationCreateRequest {
    const request: SkillSpecializationCreateRequest = {
      name: value.name,
      description: value.description,
      fillAssigneeId: value.fillAssigneeId,
    };
    if (this.canManageParticipantGroups) {
      request.expectedRevision = this.specialization.configurationRevision;
      request.crossDepartmental = value.crossDepartmental;
      request.participantGroupIds = value.participantGroupIds;
      request.responsibleGroupByDepartmentId = value.responsibleGroupByDepartmentId;
    }
    return request;
  }

  private shouldUpdateGeneralInfoOnly(value: SpecializationFormValue): boolean {
    return !this.canEditContent
      || !this.canManageCurators
      || (value.crossDepartmental && !this.specialization.crossDepartmental)
      || (value.crossDepartmental
        && value.curators.length === SPECIALIZATION_CURATOR_SELECTION.EMPTY_COUNT);
  }

  private updateGeneralInfoWithCurators(
    request: SkillSpecializationCreateRequest,
    value: SpecializationFormValue,
  ): void {
    this.facade.updateSpecializationWithCurators(this.specializationId, request, {
      curators: value.curators,
      responsibleGroupId: value.responsibleGroupId,
    })
      .takeUntil(this.specializationContextChanged$).takeUntil(this.destroy$)
      .subscribe(
        () => this.handleContextualSaveSuccess(SKILL_LEARNING_EDIT_MESSAGE.SPECIALIZATION_UPDATED),
        error => this.handleGeneralInfoSaveError(error),
      );
  }

  private updateGeneralInfo(request: SkillSpecializationCreateRequest): void {
    this.facade.updateGeneralInfo(this.specializationId, request)
      .takeUntil(this.specializationContextChanged$).takeUntil(this.destroy$)
      .subscribe(
        () => this.handleContextualSaveSuccess(SKILL_LEARNING_EDIT_MESSAGE.SPECIALIZATION_UPDATED),
        error => this.handleGeneralInfoSaveError(error),
      );
  }

  private loadTheoryDirectionsPrompt(level: number): void {
    this.facade.fetchTheoryDirectionsPrompt(this.specializationId, level)
      .takeUntil(this.destroy$)
      .subscribe(
        prompt => {
          this.theoryDirectionsPrompt = prompt.text || SKILL_LEARNING_EMPTY_TEXT;
          this.cdr.markForCheck();
        },
        () => {
          this.theoryDirectionsPrompt = SKILL_LEARNING_EMPTY_TEXT;
          this.cdr.markForCheck();
        },
      );
  }

  private handleSpecializationLoaded(spec: SkillSpecializationResponse, requestId: number): void {
    if (requestId !== this.detailRequestId) {
      return;
    }
    this.specialization = spec;
    this.selectSingleStructureDepartment();
    this.clampActiveLevelTab();
    this.isLoading = false;
    this.hasLoadError = false;
    this.isLoadDenied = false;
    this.cdr.markForCheck();
    this.loadAvailableCurators();
    this.loadGroups();
    if (this.canEditContent) {
      this.loadFillAssignees();
      this.loadTheoryDirectionsPrompt(this.activeLevelNumber);
    }
  }

  private handleLoadError(error: SkillLearningHttpError, requestId: number): void {
    if (requestId !== this.detailRequestId) {
      return;
    }
    this.isLoading = false;
    this.hasLoadError = true;
    this.isLoadDenied = !!error && error.status === SKILL_HTTP_STATUS.FORBIDDEN;
    if (!this.isLoadDenied) {
      this.toastService.error(this.translate.instant('SKILL_LEARNING_ADMIN_EDIT_LOAD_ERROR'));
    }
    this.cdr.markForCheck();
  }

  private handleContextualSaveSuccess(message: string, params?: EditSpecializationMessageParams): void {
    this.handleSaveSuccess(message, params);
    this.loadSpecialization();
  }

  private handleContentSaved(
    level: number,
    specialization?: SkillSpecializationResponse,
  ): void {
    this.resetTheoryGenerationState();
    if (specialization) {
      this.specialization = specialization;
      this.handleSaveSuccess(SKILL_LEARNING_EDIT_MESSAGE.LEVEL_SAVED, { level });
      return;
    }
    this.handleContextualSaveSuccess(SKILL_LEARNING_EDIT_MESSAGE.LEVEL_SAVED, { level });
  }

  private handleStructureSuccess(specialization: SkillSpecializationResponse): void {
    this.resetTheoryGenerationState();
    this.specialization = specialization;
    this.structureErrorKey = SKILL_LEARNING_EMPTY_TEXT;
    this.clampActiveLevelTab();
    this.loadTheoryDirectionsPrompt(this.activeLevelNumber);
    this.handleSaveSuccess(SKILL_STRUCTURE_MESSAGE.UPDATED);
  }

  private handleRequirementSuccess(specialization: SkillSpecializationResponse): void {
    this.specialization = specialization;
    this.pendingRequirementScope = null;
    this.requirementErrorKey = SKILL_LEARNING_EMPTY_TEXT;
    this.requirementErrorScope = null;
    this.toastService.success(this.translate.instant(SKILL_REQUIREMENT_MESSAGE.UPDATED));
    this.cdr.markForCheck();
  }

  private handleStructureError(error: SkillLearningHttpError): void {
    const code = this.extractErrorCode(error);
    this.structureErrorKey = this.structureErrorMessage(code, error && error.status);
    this.isSubmitting = false;
    if (code === SKILL_CONFIGURATION_ERROR.CONFLICT) {
      this.loadSpecialization();
    }
    this.cdr.markForCheck();
  }

  private handleRequirementError(
    error: SkillLearningHttpError,
    scope: SkillTrainingRequirementScope,
  ): void {
    const code = this.extractErrorCode(error);
    this.pendingRequirementScope = null;
    this.requirementErrorKey = this.requirementErrorMessage(code, error && error.status);
    this.requirementErrorScope = scope;
    if (code === SKILL_CONFIGURATION_ERROR.CONFLICT) {
      this.loadSpecialization();
    } else if (error && error.status === SKILL_HTTP_STATUS.FORBIDDEN) {
      this.denyRequirementScope(scope);
    }
    this.finishRequirementPending();
    this.cdr.markForCheck();
  }

  private extractErrorCode(error: SkillLearningHttpError): string | null {
    try {
      const body: SkillLearningErrorResponse = error && error.json ? error.json() : null;
      return body && body.code || null;
    } catch (parseError) {
      return null;
    }
  }

  private isCurrentTheoryGeneration(level: number, requestId: number): boolean {
    return this.isGeneratingTheoryTests
      && this.activeTheoryGenerationLevel === level
      && this.activeTheoryGenerationRequestId === requestId;
  }

  private completeTheoryGeneration(): void {
    this.isGeneratingTheoryTests = false;
    this.activeTheoryGenerationLevel = SKILL_LEARNING_FIRST_INDEX;
    this.activeTheoryGenerationRequestId = SKILL_LEARNING_FIRST_INDEX;
  }

  private resetTheoryGenerationState(): void {
    this.completeTheoryGeneration();
    this.generatedTheoryTestQuestionsByLevel = [];
  }

  private theoryGenerationErrorMessage(error: SkillLearningHttpError): string {
    const code = this.extractErrorCode(error);
    if (code === SKILL_TEST_ERROR_TOKEN.AI_SERVICE_UNAVAILABLE
      || (!code && !!error && error.status === SKILL_HTTP_STATUS.SERVICE_UNAVAILABLE)) {
      return SKILL_TEST_MESSAGE.SERVICE_UNAVAILABLE;
    }
    return SKILL_TEST_MESSAGE.GENERATION_ERROR;
  }

  private structureErrorMessage(code: string | null, status: number): string {
    if (code === SKILL_CONFIGURATION_ERROR.CONFLICT) {
      return SKILL_STRUCTURE_MESSAGE.CONFLICT;
    }
    if (code === SKILL_CONFIGURATION_ERROR.REQUIRED_LEVEL) {
      return SKILL_STRUCTURE_MESSAGE.REQUIRED_LEVEL;
    }
    if (code === SKILL_CONFIGURATION_ERROR.ACTIVE_TASK) {
      return SKILL_STRUCTURE_MESSAGE.ACTIVE_TASK;
    }
    return status === SKILL_HTTP_STATUS.FORBIDDEN
      ? SKILL_STRUCTURE_MESSAGE.DENIED
      : SKILL_STRUCTURE_MESSAGE.INVALID;
  }

  private requirementErrorMessage(code: string | null, status: number): string {
    if (code === HEAD_REQUIREMENT_CONFLICT) { return HEAD_REQUIREMENT_CONFLICT; }
    if (code === SKILL_CONFIGURATION_ERROR.CONFLICT) {
      return SKILL_REQUIREMENT_MESSAGE.CONFLICT;
    }
    if (code === SKILL_CONFIGURATION_ERROR.REQUIREMENT_UNPUBLISHED) {
      return SKILL_REQUIREMENT_MESSAGE.UNPUBLISHED;
    }
    return status === SKILL_HTTP_STATUS.FORBIDDEN
      ? SKILL_REQUIREMENT_MESSAGE.DENIED
      : SKILL_REQUIREMENT_MESSAGE.SAVE_ERROR;
  }

  private beginRequirementCommand(scope: SkillTrainingRequirementScope, deleting = false): boolean {
    if (this.pendingRequirementScope) {
      return false;
    }
    if (this.isLoading || this.isSubmitting) {
      this.finishRequirementPending();
      return false;
    }
    if (!this.canManageRequirement(scope, deleting)) {
      this.handleRequirementError({status: SKILL_HTTP_STATUS.FORBIDDEN}, scope);
      return false;
    }
    this.pendingRequirementScope = scope;
    this.requirementErrorKey = SKILL_LEARNING_EMPTY_TEXT;
    this.requirementErrorScope = null;
    return true;
  }

  private finishRequirementPending(): void {
    if (this.generalPage) {
      this.generalPage.finishRequirementPending();
    }
  }

  private canManageRequirement(scope: SkillTrainingRequirementScope, deleting: boolean): boolean {
    const specialization = this.specialization;
    if (!specialization) { return false; }
    if (scope === SKILL_REQUIREMENT_SCOPE.COMPANY_PROBATION) {
      return deleting ? !!specialization.canDeleteCompanyProbationRequirement : !!specialization.canManageCompanyProbationRequirement;
    }
    if (scope === SKILL_REQUIREMENT_SCOPE.DEPARTMENT_PROBATION) {
      return !!this.selectedDepartmentId && !!specialization.canManageDepartmentProbationRequirement;
    }
    if (scope === SKILL_REQUIREMENT_SCOPE.CURRENT_HEAD) {
      return !!specialization.canManageCurrentHeadRequirement;
    }
    return scope === SKILL_REQUIREMENT_SCOPE.FUTURE_HEAD && !!specialization.canManageFutureHeadRequirement;
  }

  private denyRequirementScope(scope: SkillTrainingRequirementScope): void {
    if (!this.specialization) { return; }
    this.specialization = Object.assign({}, this.specialization);
    if (scope === SKILL_REQUIREMENT_SCOPE.COMPANY_PROBATION) {
      this.specialization.canManageCompanyProbationRequirement = false;
      this.specialization.canDeleteCompanyProbationRequirement = false;
    }
    if (scope === SKILL_REQUIREMENT_SCOPE.DEPARTMENT_PROBATION) {
      this.specialization.canManageDepartmentProbationRequirement = false;
    }
    if (scope === SKILL_REQUIREMENT_SCOPE.FUTURE_HEAD) {
      this.specialization.canManageFutureHeadRequirement = false;
    }
    if (scope === SKILL_REQUIREMENT_SCOPE.CURRENT_HEAD) {
      this.specialization.canManageCurrentHeadRequirement = false;
    }
  }

  private selectSingleStructureDepartment(): void {
    const ids = this.specialization.allowedStructureDepartmentIds || [];
    if (!this.selectedDepartmentId && ids.length === SKILL_LEARNING_LEVEL_COUNT.ONE) {
      this.applyDepartmentChange(ids[SKILL_LEARNING_FIRST_INDEX]);
    }
  }

  private clampActiveLevelTab(): void {
    const count = this.specialization.configuredLevelCount || SKILL_LEARNING_LEVEL_COUNT.ONE;
    const maxTab = count - SKILL_LEARNING_LEVEL_COUNT.ONE;
    if (this.activeLevelTab > maxTab) {
      this.activeLevelTab = <SkillLearningLevelTab>maxTab;
    }
  }

  private applyDepartmentChange(departmentId: string): void {
    this.selectedDepartmentId = departmentId;
    this.curatorRequestId += SKILL_LEARNING_LEVEL_COUNT.ONE;
    this.availableCurators = [];
    this.navigateWithDepartmentContext(departmentId);
    if (this.specializationId) {
      this.loadSpecialization();
    }
  }

  private navigateWithDepartmentContext(departmentId: string): void {
    const queryParams: Params = {};
    if (departmentId) {
      queryParams[SKILL_DEPARTMENT_QUERY_PARAM] = departmentId;
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
    });
  }

  get departmentQueryParams(): Params {
    const params: Params = {};
    if (this.selectedDepartmentId) {
      params[SKILL_DEPARTMENT_QUERY_PARAM] = this.selectedDepartmentId;
    }
    return params;
  }

  private focusRequirements(): void {
    if (this.generalPage) {
      this.generalPage.focusRequirements();
    }
  }

  private handleGroupsLoadError(): void {
    this.toastService.error(this.translate.instant('SKILL_LEARNING_ADMIN_EDIT_LOAD_ERROR'));
    this.cdr.markForCheck();
  }

  private handleSaveSuccess(message: string, params?: EditSpecializationMessageParams): void {
    this.isSubmitting = false;
    this.toastService.success(this.translate.instant(message, params));
    this.cdr.markForCheck();
  }

  private handleSaveError(): void {
    this.isSubmitting = false;
    this.toastService.error(this.translate.instant('SKILL_LEARNING_ADMIN_EDIT_SAVE_ERROR'));
    this.cdr.markForCheck();
  }

  private handleCuratorSaveError(error: SkillLearningHttpError): void {
    if (error && error.status === SKILL_HTTP_STATUS.FORBIDDEN && this.specialization) {
      this.specialization = Object.assign({}, this.specialization, { canManageCurators: false });
      this.clearAvailableCurators();
    }
    this.handleSaveError();
  }

  private handleGeneralInfoSaveError(error: SkillLearningHttpError): void {
    if (error && error.status === SKILL_HTTP_STATUS.FORBIDDEN) {
      this.hasLoadError = true;
      this.isLoadDenied = true;
    }
    this.handleCuratorSaveError(error);
  }

  private handleContentSaveError(error: SkillLearningHttpError): void {
    this.isSubmitting = false;
    if (this.extractErrorCode(error) === SKILL_CONFIGURATION_ERROR.CONFLICT) {
      this.toastService.error(this.translate.instant(SKILL_LEARNING_EDIT_MESSAGE.CONTENT_CONFLICT));
      this.loadSpecialization();
      return;
    }
    this.handleSaveError();
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/components/admin/edit-specialization-container/edit-specialization-container.component.ts